import { useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { generate } from '../hooks/useAI';
import { apiUrl } from '../lib/api-url';

interface Block {
  id: string;
  type: string;
  content: any;
  styles: any;
}

const blockTypes = [
  { type: 'text', name: 'Text', icon: 'T' },
  { type: 'image', name: 'Image', icon: '🖼' },
  { type: 'button', name: 'Button', icon: '⬚' },
  { type: 'divider', name: 'Divider', icon: '—' },
  { type: 'spacer', name: 'Spacer', icon: '⬓' },
  { type: 'hero', name: 'Hero', icon: '🎯' },
  { type: 'features', name: 'Features', icon: '★' },
  { type: 'footer', name: 'Footer', icon: '⚓' },
];

export default function EmailBuilder() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [blocks, setBlocks] = useState<Block[]>([]);
  const [selectedBlock, setSelectedBlock] = useState<string | null>(null);
  const [campaignName, setCampaignName] = useState('');
  const [subject, setSubject] = useState('');
  const [previewText, setPreviewText] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showAIModal, setShowAIModal] = useState<'subject' | 'email' | 'improve' | 'tone' | 'shorten' | null>(null);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);
  const [aiResult, setAiResult] = useState<any>(null);
  const dragItem = useRef<number | null>(null);
  const dragOverItem = useRef<number | null>(null);

  const addBlock = (type: string) => {
    const newBlock: Block = {
      id: `block-${Date.now()}`,
      type,
      content: getDefaultContent(type),
      styles: getDefaultStyles(type),
    };
    setBlocks([...blocks, newBlock]);
    setSelectedBlock(newBlock.id);
  };

  const getDefaultContent = (type: string) => {
    switch (type) {
      case 'text':
        return { text: 'Enter your text here...' };
      case 'image':
        return { src: '', alt: 'Image', link: '' };
      case 'button':
        return { text: 'Click Here', link: '' };
      case 'hero':
        return { title: 'Hero Title', subtitle: 'Hero subtitle', image: '', ctaText: 'Learn More', ctaLink: '' };
      case 'features':
        return { title: 'Features', items: [{ icon: '✓', text: 'Feature 1' }, { icon: '✓', text: 'Feature 2' }] };
      case 'footer':
        return { company: '', address: '', unsubscribe: true };
      default:
        return {};
    }
  };

  const getDefaultStyles = (type: string) => {
    const base = { padding: 20, backgroundColor: 'transparent' };
    switch (type) {
      case 'text':
        return { ...base, fontSize: 16, color: '#333333', textAlign: 'left', lineHeight: 1.6 };
      case 'button':
        return { ...base, backgroundColor: '#3B82F6', color: '#FFFFFF', borderRadius: 8, textAlign: 'center' };
      case 'hero':
        return { ...base, backgroundColor: '#F3F4F6', textAlign: 'center' };
      default:
        return base;
    }
  };

  const updateBlock = (id: string, content: any, styles?: any) => {
    setBlocks(blocks.map(b => b.id === id ? { ...b, content, styles: styles || b.styles } : b));
  };

  const deleteBlock = (id: string) => {
    setBlocks(blocks.filter(b => b.id !== id));
    setSelectedBlock(null);
  };

  const handleDragStart = (index: number) => {
    dragItem.current = index;
  };

  const handleDragEnter = (index: number) => {
    dragOverItem.current = index;
  };

  const handleDragEnd = () => {
    if (dragItem.current === null || dragOverItem.current === null) return;
    
    const newBlocks = [...blocks];
    const draggedItem = newBlocks[dragItem.current];
    newBlocks.splice(dragItem.current, 1);
    newBlocks.splice(dragOverItem.current, 0, draggedItem);
    
    setBlocks(newBlocks);
    dragItem.current = null;
    dragOverItem.current = null;
  };

  const handleAIGenerate = async () => {
    if (!aiPrompt.trim()) return;
    setAiLoading(true);
    setAiError(null);
    setAiResult(null);
    try {
      if (showAIModal === 'subject') {
        const result = await generate('subject_line', aiPrompt, { subject, campaignName });
        const output = typeof result.output === 'string' ? JSON.parse(result.output) : result.output;
        setAiResult({ type: 'subjects', data: Array.isArray(output) ? output : [output] });
      } else if (showAIModal === 'email' || showAIModal === 'improve' || showAIModal === 'tone' || showAIModal === 'shorten') {
        const currentContent = blocks.map(b => b.content.text || '').join('\n');
        const prompt = showAIModal === 'email'
          ? aiPrompt
          : showAIModal === 'improve'
            ? `Improve this email content:\n\n${currentContent}\n\n${aiPrompt}`
            : showAIModal === 'tone'
              ? `Rewrite this email in a ${aiPrompt} tone:\n\n${currentContent}`
              : `Shorten this email content:\n\n${currentContent}\n\n${aiPrompt}`;
        const result = await generate('email_body', prompt, { subject, campaignName });
        const output = typeof result.output === 'string' ? result.output : JSON.stringify(result.output);
        setAiResult({ type: 'email', data: output });
      }
    } catch (err: any) {
      setAiError(err.message || 'AI generation failed');
    } finally {
      setAiLoading(false);
    }
  };

  const applyAISubject = (s: string) => {
    setSubject(s);
    setShowAIModal(null);
    setAiPrompt('');
    setAiResult(null);
  };

  const applyAIEmail = () => {
    if (aiResult?.data) {
      const textBlock: Block = {
        id: `block-${Date.now()}`,
        type: 'text',
        content: { text: aiResult.data },
        styles: getDefaultStyles('text'),
      };
      setBlocks([...blocks, textBlock]);
      setSelectedBlock(textBlock.id);
    }
    setShowAIModal(null);
    setAiPrompt('');
    setAiResult(null);
  };

  const saveCampaign = async () => {
    setIsSaving(true);
    try {
      const token = localStorage.getItem('access_token');
      const contentHtml = generateHTML(blocks);
      
      const response = await fetch(apiUrl(`/api/v1/campaigns/${id || 'new'}`), {
        method: id ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          name: campaignName,
          subject,
          preview_text: previewText,
          content_html: contentHtml,
          blocks: blocks,
        }),
      });

      if (response.ok) {
        alert('Campaign saved successfully!');
        navigate('/campaigns');
      }
    } catch (error) {
      console.error('Save error:', error);
      alert('Failed to save campaign');
    } finally {
      setIsSaving(false);
    }
  };

  const generateHTML = (blocks: Block[]) => {
    return `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${subject}</title>
</head>
<body style="margin: 0; padding: 0; font-family: Arial, sans-serif; background-color: #f4f4f4;">
  <table role="presentation" style="width: 100%; border-collapse: collapse;">
    <tr>
      <td align="center" style="padding: 20px;">
        <table role="presentation" style="max-width: 600px; width: 100%; border-collapse: collapse; background-color: #ffffff;">
          ${blocks.map(block => renderBlock(block)).join('\n')}
        </table>
      </td>
    </tr>
  </table>
</body>
</html>
    `.trim();
  };

  const renderBlock = (block: Block) => {
    switch (block.type) {
      case 'text':
        return `
          <tr>
            <td style="padding: ${block.styles.padding}px; text-align: ${block.styles.textAlign}; color: ${block.styles.color}; font-size: ${block.styles.fontSize}px; line-height: ${block.styles.lineHeight};">
              ${block.content.text}
            </td>
          </tr>
        `;
      case 'image':
        return `
          <tr>
            <td align="${block.styles.textAlign || 'center'}" style="padding: ${block.styles.padding}px;">
              <img src="${block.content.src}" alt="${block.content.alt}" style="max-width: 100%; height: auto;">
            </td>
          </tr>
        `;
      case 'button':
        return `
          <tr>
            <td align="center" style="padding: ${block.styles.padding}px;">
              <a href="${block.content.link}" style="display: inline-block; padding: 12px 32px; background-color: ${block.styles.backgroundColor}; color: ${block.styles.color}; text-decoration: none; border-radius: ${block.styles.borderRadius}px; font-weight: bold;">
                ${block.content.text}
              </a>
            </td>
          </tr>
        `;
      case 'divider':
        return `
          <tr>
            <td style="padding: ${block.styles.padding}px;">
              <hr style="border: none; border-top: 1px solid #e0e0e0;">
            </td>
          </tr>
        `;
      case 'spacer':
        return `
          <tr>
            <td style="height: ${block.styles.height || 20}px;"></td>
          </tr>
        `;
      case 'hero':
        return `
          <tr>
            <td style="padding: ${block.styles.padding}px; text-align: ${block.styles.textAlign}; background-color: ${block.styles.backgroundColor};">
              ${block.content.image ? `<img src="${block.content.image}" alt="" style="max-width: 100%; height: auto; margin-bottom: 20px;">` : ''}
              <h1 style="margin: 0 0 10px 0; font-size: 32px; color: #333333;">${block.content.title}</h1>
              <p style="margin: 0 0 20px 0; font-size: 18px; color: #666666;">${block.content.subtitle}</p>
              <a href="${block.content.ctaLink}" style="display: inline-block; padding: 12px 32px; background-color: #3B82F6; color: #FFFFFF; text-decoration: none; border-radius: 8px; font-weight: bold;">
                ${block.content.ctaText}
              </a>
            </td>
          </tr>
        `;
      case 'footer':
        return `
          <tr>
            <td style="padding: ${block.styles.padding}px; text-align: center; background-color: #f4f4f4; font-size: 12px; color: #666666;">
              ${block.content.company ? `<p style="margin: 0 0 10px 0;"><strong>${block.content.company}</strong></p>` : ''}
              ${block.content.address ? `<p style="margin: 0 0 10px 0;">${block.content.address}</p>` : ''}
              ${block.content.unsubscribe ? `<p style="margin: 0;"><a href="{{unsubscribe_url}}" style="color: #666666;">Unsubscribe</a></p>` : ''}
            </td>
          </tr>
        `;
      default:
        return '';
    }
  };

  const SelectedBlockEditor = () => {
    const block = blocks.find(b => b.id === selectedBlock);
    if (!block) return null;

    return (
      <div className="bg-white p-4 border-l border-gray-200 w-80 overflow-y-auto">
        <h3 className="font-semibold text-gray-900 mb-4 capitalize">{block.type} Block</h3>
        
        {block.type === 'text' && (
          <>
            <label className="block text-sm font-medium text-gray-700 mb-2">Text</label>
            <textarea
              className="w-full border border-gray-300 rounded-lg p-2 text-sm"
              rows={6}
              value={block.content.text}
              onChange={(e) => updateBlock(block.id, { text: e.target.value })}
            />
            <label className="block text-sm font-medium text-gray-700 mt-4 mb-2">Font Size</label>
            <input
              type="number"
              className="w-full border border-gray-300 rounded-lg p-2 text-sm"
              value={block.styles.fontSize}
              onChange={(e) => updateBlock(block.id, block.content, { ...block.styles, fontSize: parseInt(e.target.value) })}
            />
          </>
        )}

        {block.type === 'image' && (
          <>
            <label className="block text-sm font-medium text-gray-700 mb-2">Image URL</label>
            <input
              type="text"
              className="w-full border border-gray-300 rounded-lg p-2 text-sm"
              value={block.content.src || ''}
              onChange={(e) => updateBlock(block.id, { ...block.content, src: e.target.value })}
              placeholder="https://example.com/image.jpg"
            />
            <label className="block text-sm font-medium text-gray-700 mt-4 mb-2">Link URL</label>
            <input
              type="text"
              className="w-full border border-gray-300 rounded-lg p-2 text-sm"
              value={block.content.link || ''}
              onChange={(e) => updateBlock(block.id, { ...block.content, link: e.target.value })}
            />
          </>
        )}

        {block.type === 'button' && (
          <>
            <label className="block text-sm font-medium text-gray-700 mb-2">Button Text</label>
            <input
              type="text"
              className="w-full border border-gray-300 rounded-lg p-2 text-sm"
              value={block.content.text}
              onChange={(e) => updateBlock(block.id, { ...block.content, text: e.target.value })}
            />
            <label className="block text-sm font-medium text-gray-700 mt-4 mb-2">Link URL</label>
            <input
              type="text"
              className="w-full border border-gray-300 rounded-lg p-2 text-sm"
              value={block.content.link || ''}
              onChange={(e) => updateBlock(block.id, { ...block.content, link: e.target.value })}
            />
            <label className="block text-sm font-medium text-gray-700 mt-4 mb-2">Background Color</label>
            <input
              type="color"
              className="w-full border border-gray-300 rounded-lg p-1 text-sm h-10"
              value={block.styles.backgroundColor}
              onChange={(e) => updateBlock(block.id, block.content, { ...block.styles, backgroundColor: e.target.value })}
            />
          </>
        )}

        <button
          onClick={() => deleteBlock(block.id)}
          className="w-full mt-6 px-4 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 text-sm font-medium"
        >
          Delete Block
        </button>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="flex items-center justify-between h-16 px-4">
          <div className="flex items-center space-x-4">
            <button onClick={() => navigate('/campaigns')} className="text-gray-500 hover:text-gray-700">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
            </button>
            <input
              type="text"
              placeholder="Campaign Name"
              className="text-lg font-semibold border-none focus:ring-0 w-64"
              value={campaignName}
              onChange={(e) => setCampaignName(e.target.value)}
            />
          </div>
          <div className="flex items-center space-x-3">
            <div className="relative group">
              <button className="px-4 py-2 text-sm font-medium text-purple-700 bg-purple-50 border border-purple-200 rounded-lg hover:bg-purple-100 flex items-center gap-1.5">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
                AI
              </button>
              <div className="absolute right-0 top-full mt-1 w-56 bg-white border border-gray-200 rounded-xl shadow-lg py-1 hidden group-hover:block z-50">
                <button onClick={() => setShowAIModal('subject')} className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">Generate Subject Lines</button>
                <button onClick={() => setShowAIModal('email')} className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">Write Email</button>
                <button onClick={() => setShowAIModal('improve')} className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">Improve Email</button>
                <button onClick={() => setShowAIModal('tone')} className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">Rewrite in Tone</button>
                <button onClick={() => setShowAIModal('shorten')} className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">Shorten / Expand</button>
              </div>
            </div>
            <button
              onClick={() => setShowPreview(!showPreview)}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              {showPreview ? 'Hide Preview' : 'Preview'}
            </button>
            <button
              onClick={saveCampaign}
              disabled={isSaving || !campaignName || !subject}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? 'Saving...' : 'Save Campaign'}
            </button>
          </div>
        </div>
        <div className="px-4 pb-4 flex items-center space-x-4">
          <input
            type="text"
            placeholder="Email Subject"
            className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
          />
          <input
            type="text"
            placeholder="Preview Text"
            className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm"
            value={previewText}
            onChange={(e) => setPreviewText(e.target.value)}
          />
        </div>
      </header>

      <div className="flex h-[calc(100vh-8rem)]">
        {/* Block Palette */}
        <div className="w-48 bg-white border-r border-gray-200 p-4 overflow-y-auto">
          <h3 className="text-sm font-semibold text-gray-900 mb-4">Blocks</h3>
          <div className="space-y-2">
            {blockTypes.map((blockType) => (
              <button
                key={blockType.type}
                onClick={() => addBlock(blockType.type)}
                className="w-full flex items-center px-3 py-2 text-sm text-gray-700 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <span className="mr-2">{blockType.icon}</span>
                {blockType.name}
              </button>
            ))}
          </div>

          <h3 className="text-sm font-semibold text-gray-900 mt-6 mb-4">Personalization</h3>
          <div className="space-y-2">
            {['{{first_name}}', '{{last_name}}', '{{email}}', '{{company}}'].map((tag) => (
              <button
                key={tag}
                onClick={() => {
                  // Add tag to selected text block
                  const textBlock = blocks.find(b => b.type === 'text' && b.id === selectedBlock);
                  if (textBlock) {
                    updateBlock(textBlock.id, { text: textBlock.content.text + ' ' + tag });
                  }
                }}
                className="w-full px-3 py-2 text-xs font-mono text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 text-left"
              >
                {tag}
              </button>
            ))}
          </div>
        </div>

        {/* Canvas */}
        <div className="flex-1 p-8 overflow-y-auto bg-gray-100">
          <div className="max-w-2xl mx-auto bg-white min-h-[600px] shadow-lg">
            {blocks.length === 0 ? (
              <div className="flex items-center justify-center h-64 text-gray-400">
                <div className="text-center">
                  <svg className="w-16 h-16 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  <p>Drag blocks here or click to add</p>
                </div>
              </div>
            ) : (
              <div className="p-4">
                {blocks.map((block, index) => (
                  <div
                    key={block.id}
                    draggable
                    onDragStart={() => handleDragStart(index)}
                    onDragEnter={() => handleDragEnter(index)}
                    onDragEnd={handleDragEnd}
                    onClick={() => setSelectedBlock(block.id)}
                    className={`relative mb-2 cursor-move group ${
                      selectedBlock === block.id ? 'ring-2 ring-blue-500' : 'hover:ring-2 hover:ring-blue-300'
                    }`}
                  >
                    <div className="absolute -left-8 top-1/2 transform -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity">
                      <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
                      </svg>
                    </div>
                    <div className="p-4 bg-white border border-gray-200">
                      {renderBlock(block)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Properties Panel */}
        {selectedBlock && <SelectedBlockEditor />}
      </div>

      {/* Preview Modal */}
      {showPreview && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold">Email Preview</h3>
              <button onClick={() => setShowPreview(false)} className="text-gray-500 hover:text-gray-700">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-8 overflow-y-auto max-h-[calc(90vh-80px)]">
              <div
                className="max-w-xl mx-auto"
                dangerouslySetInnerHTML={{ __html: generateHTML(blocks) }}
              />
            </div>
          </div>
        </div>
      )}

      {/* AI Modal */}
      {showAIModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-lg w-full max-h-[80vh] overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
                {showAIModal === 'subject' && 'Generate Subject Lines'}
                {showAIModal === 'email' && 'Write Email with AI'}
                {showAIModal === 'improve' && 'Improve Email'}
                {showAIModal === 'tone' && 'Rewrite in Tone'}
                {showAIModal === 'shorten' && 'Shorten / Expand'}
              </h3>
              <button onClick={() => { setShowAIModal(null); setAiPrompt(''); setAiResult(null); setAiError(null); }} className="text-gray-500 hover:text-gray-700">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-4 overflow-y-auto max-h-[calc(80vh-80px)]">
              {showAIModal === 'subject' && (
                <p className="text-sm text-gray-500 mb-3">Describe your email and AI will generate 5 subject line options.</p>
              )}
              {showAIModal === 'email' && (
                <p className="text-sm text-gray-500 mb-3">Describe the email you want to create. Include topic, audience, and goal.</p>
              )}
              {showAIModal === 'improve' && (
                <p className="text-sm text-gray-500 mb-3">Tell AI how to improve your email (e.g., "make it more engaging", "add urgency").</p>
              )}
              {showAIModal === 'tone' && (
                <p className="text-sm text-gray-500 mb-3">Enter the tone you want (e.g., professional, casual, urgent, friendly).</p>
              )}
              {showAIModal === 'shorten' && (
                <p className="text-sm text-gray-500 mb-3">Tell AI to shorten or expand your email content.</p>
              )}
              <textarea
                className="w-full border border-gray-300 rounded-lg p-3 text-sm mb-4"
                rows={4}
                placeholder={
                  showAIModal === 'subject' ? "e.g., Weekly newsletter about our new product launch targeting existing customers" :
                  showAIModal === 'tone' ? "e.g., professional, friendly, urgent" :
                  showAIModal === 'shorten' ? "e.g., make it 30% shorter" :
                  "Describe what you want..."
                }
                value={aiPrompt}
                onChange={(e) => setAiPrompt(e.target.value)}
              />
              {aiError && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-600">{aiError}</p>
                </div>
              )}
              {aiResult?.type === 'subjects' && (
                <div className="space-y-2 mb-4">
                  {aiResult.data.map((s: string, i: number) => (
                    <button
                      key={i}
                      onClick={() => applyAISubject(s)}
                      className="w-full text-left p-3 bg-gray-50 rounded-lg hover:bg-blue-50 hover:border-blue-300 border border-transparent transition-colors"
                    >
                      <p className="text-sm text-gray-900">{s}</p>
                      <p className="text-xs text-blue-600 mt-1">Click to use</p>
                    </button>
                  ))}
                </div>
              )}
              {aiResult?.type === 'email' && (
                <div className="mb-4">
                  <div className="bg-gray-50 rounded-lg p-4 max-h-64 overflow-y-auto mb-3">
                    <pre className="text-sm text-gray-700 whitespace-pre-wrap font-sans">{aiResult.data}</pre>
                  </div>
                  <button
                    onClick={applyAIEmail}
                    className="w-full px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700"
                  >
                    Add to Email
                  </button>
                </div>
              )}
            </div>
            <div className="p-4 border-t bg-gray-50 flex justify-end gap-2">
              <button
                onClick={() => { setShowAIModal(null); setAiPrompt(''); setAiResult(null); setAiError(null); }}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={handleAIGenerate}
                disabled={aiLoading || !aiPrompt.trim()}
                className="px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {aiLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                    Generating...
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                    </svg>
                    Generate
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
